import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { salvaBlob } from '../../utils/documenti';
import Icona from '../../components/cliente/Icone';
import { Iniziale } from '../../components/staff/Persone';
import { ROTTE as ROTTE_PROVVIGIONI, soldi } from '../../components/staff/Provvigione';
import '../../components/staff/cruscotto-mese.css';
import '../../components/staff/provvigioni.css';

// Il cruscotto del mese, una pagina sola per due indirizzi:
// - /collaboratore/mese: il collaboratore vede i suoi numeri (il responsabile di gruppo può scegliere un membro);
// - /admin/collaboratori/:id: l'admin (e il responsabile, per il suo gruppo) vede quelli di una persona.
// Dentro: il mese fra gli ultimi 12, gli otto numeri del mese, la quota di provvigioni del mese, i tempi medi, il grafico a barre dei sei principali,
// la lista dei clienti del mese (solo nome, fase e ultimo evento: mai email, telefono, budget o note), il totale del
// gruppo con la riga di ogni membro e l'esportazione CSV.
// Dati: GET /api/cruscotto, /api/cruscotto/gruppo, /api/cruscotto.csv (backend/cruscotto.py).

export const ROTTA = '/cruscotto';
export const ROTTA_GRUPPO = '/cruscotto/gruppo';
export const ROTTA_CSV = '/cruscotto.csv';

// I numeri del mese, nell'ordine in cui si leggono (le chiavi sono quelle del backend)
export const NUMERI = [
  ['clienti_nuovi', 'Clienti nuovi', 'oro'],
  ['clienti_attivi', 'Clienti attivi', ''],
  ['visite', 'Visite fatte', 'blu'],
  ['proposte', 'Proposte', 'viola'],
  ['proposte_accettate', 'Proposte accettate', 'verde'],
  ['rogiti', 'Rogiti', 'verde'],
  ['documenti_verificati', 'Documenti verificati', 'blu'],
  ['telefonate', 'Telefonate con esito', ''],
];
// I sei del grafico: il percorso del mese, da quanti clienti sono arrivati a quante case sono diventate loro.
// Fuori restano i clienti attivi (è quanti ne segue, non quanto è successo) e le telefonate.
export const SEI = ['clienti_nuovi', 'visite', 'proposte', 'proposte_accettate', 'rogiti', 'documenti_verificati'];

const nomeNumero = (chiave) => (NUMERI.find(([k]) => k === chiave) || [, chiave])[1];
const coloreNumero = (chiave) => (NUMERI.find(([k]) => k === chiave) || [, , ''])[2];
const quanti = (numeri, chiave) => Number(numeri?.[chiave] || 0);

// Gli ultimi 12 mesi, dal più recente: {valore: "2026-09", etichetta: "settembre 2026"}. Li manda il server (dal più
// vecchio); questi servono appena si apre la pagina, prima che la risposta arrivi.
export const mesiRecenti = (oggi = new Date()) => Array.from({ length: 12 }, (_, i) => {
  const d = new Date(oggi.getFullYear(), oggi.getMonth() - i, 1);
  return {
    valore: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
    etichetta: d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }),
  };
});
const mesiDelServer = (mesi) => (Array.isArray(mesi) && mesi.length
  ? [...mesi].map((m) => ({ valore: m.mese, etichetta: m.etichetta })).reverse()
  : null);

const etichettaMese = (valore, mesi) => mesi.find((m) => m.valore === valore)?.etichetta || valore;

// «31 giorni», «—» quando nel mese non c'è nessun caso su cui fare la media
const giorni = (n, casi) => (n === null || n === undefined || casi === 0 ? '—' : `${n} ${n === 1 ? 'giorno' : 'giorni'}`);
const suCasi = (casi) => (casi ? ` · ${casi} ${casi === 1 ? 'caso' : 'casi'}` : '');

const TAG_FASE = { ricerca: 't-b', visita: 't-o', proposta: 't-v', mutuo: 't-w', rogito: 't-g' };
const nomeFase = (fase) => fase?.nome || '—';
const tagFase = (fase) => TAG_FASE[fase?.chiave] || 't-b';

// L'ultimo evento: la parola che manda il server e il giorno ("Proposta il 12 settembre"). Mai le note dello staff.
export const testoEvento = (evento) => {
  if (!evento) return '';
  const d = evento.data ? new Date(evento.data) : null;
  const quando = d && !Number.isNaN(d.getTime()) ? ` il ${d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}` : '';
  return `${evento.testo || evento.tipo || ''}${quando}`.trim();
};

// La quota del mese: quanto spetta di provvigioni a chi si sta guardando. Sono le sue fette (previsto, fatturato,
// incassato), mai il compenso del cliente né i conti con le agenzie: il server manda solo quelle che si possono
// vedere: la porta il cruscotto stesso (`quota`) o, se non c'è, GET /api/provvigioni/collaboratori?mese=…
// (backend/provvigioni.py). Senza pratiche nel mese, o senza le
// provvigioni collegate, la card non compare: il cruscotto è dei conteggi e resta com'è.
const QUOTE = [['previsto', 'prevista'], ['fatturato', 'fatturata'], ['incassato', 'incassata']];

// La quota che arriva col cruscotto: un numero solo (`quota`) o i tre importi
export const quotaDa = (valore) => {
  if (valore === null || valore === undefined) return null;
  if (typeof valore === 'object') return valore;
  return { incassato: valore, sola: true };
};

const QuotaDelMese = ({ mese, persona, gia, nome }) => {
  const [riga, setRiga] = useState(() => quotaDa(gia));
  useEffect(() => {
    if (gia !== null && gia !== undefined) { setRiga(quotaDa(gia)); return undefined; }
    let vivo = true;
    setRiga(null);
    axios.get(`${API}${ROTTE_PROVVIGIONI.collaboratori}`, { params: { mese } })
      .then((r) => {
        if (!vivo || !Array.isArray(r.data?.collaboratori)) return;
        setRiga(r.data.collaboratori.find((c) => c.persona_id === persona) || null);
      })
      .catch(() => { if (vivo) setRiga(null); });
    return () => { vivo = false; };
  }, [mese, persona, gia]);
  if (!riga) return null;
  if (riga.sola) {
    return (
      <section className="card r" data-testid="quota-mese" style={{ marginTop: '1rem' }}>
        <div className="ttl"><div className="i"><Icona nome="euro" /></div><h2>La quota del mese</h2>
          <Link className="link" to={`/admin/provvigioni?anno=${mese.slice(0, 4)}&mese=${mese}`}>Tutte le provvigioni</Link></div>
        <div className="prov-quota"><div><b>{soldi(riga.incassato)}</b><span>la vostra quota</span></div></div>
      </section>
    );
  }
  return (
    <section className="card r" data-testid="quota-mese" style={{ marginTop: '1rem' }}>
      <div className="ttl"><div className="i"><Icona nome="euro" /></div><h2>La quota del mese</h2>
        <Link className="link" to={`/admin/provvigioni?anno=${mese.slice(0, 4)}&mese=${mese}`}>Tutte le provvigioni</Link></div>
      <div className="prov-quota">
        {QUOTE.map(([k, nome]) => <div key={k}><b>{soldi(riga[k])}</b><span>{nome}</span></div>)}
        <div><b>{riga.pratiche || 0}</b><span>{riga.pratiche === 1 ? 'pratica' : 'pratiche'}</span></div>
      </div>
      <p className="imp-nota">{nome ? `È la fetta di ${nome}` : 'È la vostra fetta'} dell'incasso netto delle pratiche del mese: il resto lo vede chi si occupa dei conti.</p>
    </section>
  );
};

// Il grafico: sei barre lunghe in proporzione alla più alta (niente librerie, solo dei div)
const Barre = ({ numeri }) => {
  const massimo = Math.max(1, ...SEI.map((k) => quanti(numeri, k)));
  return (
    <div className="barre" data-testid="grafico-mese">
      {SEI.map((k) => (
        <div key={k} data-testid={`barra-${k}`}>
          <span>{nomeNumero(k)}</span>
          <div className="barra" role="img" aria-label={`${nomeNumero(k)}: ${quanti(numeri, k)}`}>
            <i className={coloreNumero(k)} style={{ width: `${(quanti(numeri, k) / massimo) * 100}%` }} />
          </div>
          <b>{quanti(numeri, k)}</b>
        </div>
      ))}
    </div>
  );
};

// Perché la pagina non mostra i numeri: il server lo dice quasi sempre lui (detail), il resto sono i casi nostri
const GENERICI = {
  400: 'Di quel mese non ci sono i numeri: si possono guardare solo gli ultimi 12 mesi.',
  403: 'Questi numeri non si possono vedere: ognuno vede i suoi, il responsabile anche quelli del suo gruppo.',
  404: 'Non trovo i numeri di questa persona.',
  vuoto: 'Il server ha risposto senza i numeri del mese. La pagina è pronta e li mostra appena arrivano.',
  errore: 'Non riesco a caricare i numeri del mese. Riprovate tra poco.',
};
const perche = (e) => {
  const stato = e?.response?.status;
  const detail = e?.response?.data?.detail;
  // Un 400/403/404 porta un messaggio scritto per chi legge, e si mostra così com'è. Un errore del server (5xx)
  // no: quello che dice è roba sua ("boom", un'eccezione) e a video finiva tale e quale — lo stesso difetto già
  // corretto nelle provvigioni (blocco 3), qui in una seconda copia (blocco 4, primo giro).
  if (typeof detail === 'string' && detail.trim() && !(stato >= 500)) return detail;
  return GENERICI[stato] || GENERICI.errore;
};

const CruscottoMese = () => {
  const { userType, user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [mesi, setMesi] = useState(() => mesiRecenti());
  const [dati, setDati] = useState(null);
  const [gruppo, setGruppo] = useState(null);
  const [persone, setPersone] = useState([]); // chi si può scegliere: resta mentre si cambia mese o persona
  const [problema, setProblema] = useState(null);

  const mese = params.get('mese') || mesi[0].valore;
  const persona = id || params.get('persona') || '';
  const isAdmin = userType === 'admin';
  const io = user?.id;

  useEffect(() => {
    let vivo = true;
    setDati(null);
    setGruppo(null);
    setProblema(null);
    const richiesta = { params: { persona: persona || undefined, mese } };
    axios.get(`${API}${ROTTA}`, richiesta)
      .then((r) => {
        if (!vivo) return;
        // Se la risposta non ha i numeri dentro (in locale il sito e le rotte stanno sullo stesso indirizzo, e una
        // rotta che non c'è torna la pagina del sito) la pagina lo dice invece di riempirsi di zeri
        if (!r.data || typeof r.data.numeri !== 'object') { setProblema(GENERICI.vuoto); return; }
        setDati(r.data);
        if (mesiDelServer(r.data.mesi)) setMesi(mesiDelServer(r.data.mesi));
        if (Array.isArray(r.data.persone)) setPersone(r.data.persone);
        // Il gruppo è una seconda richiesta, e riguarda SEMPRE il gruppo di chi guarda, non la persona guardata:
        // il responsabile che apre il mese di un suo collaboratore vuole comunque vedere il totale del suo gruppo.
        // Prima si chiedeva con la persona guardata e tornava 403 (un membro non è responsabile) o 404 (chi non ha
        // gruppo), con l'errore rosso in console e niente da mostrare.
        if (r.data.puo_vedere_gruppo) {
          axios.get(`${API}${ROTTA_GRUPPO}`, { params: { mese } })
            .then((g) => { if (vivo && Array.isArray(g.data?.membri)) setGruppo(g.data); })
            .catch(() => { if (vivo) setGruppo(null); });
        }
      })
      .catch((e) => { if (vivo) setProblema(perche(e)); });
    return () => { vivo = false; };
  }, [persona, mese, io]);

  // Il mese sta nell'indirizzo: si può mandare il link e tornarci indietro
  const cambiaMese = (valore) => {
    const nuovi = { mese: valore };
    if (!id && persona) nuovi.persona = persona;
    setParams(nuovi);
  };
  // L'admin passa alla pagina della persona; il responsabile resta qui e cambia solo ?persona=
  const cambiaPersona = (chi) => {
    if (isAdmin) navigate(chi && chi !== io ? `/admin/collaboratori/${chi}?mese=${mese}` : `/collaboratore/mese?mese=${mese}`);
    else setParams(chi && chi !== io ? { persona: chi, mese } : { mese });
  };

  // Il CSV lo scrive il server (";" e BOM, si apre con Excel), con o senza il gruppo
  const scarica = async (delGruppo = false) => {
    try {
      const r = await axios.get(`${API}${ROTTA_CSV}`, {
        params: { persona: persona || undefined, mese, gruppo: delGruppo || undefined },
        responseType: 'blob',
      });
      salvaBlob(r.data, `cruscotto-${mese}${delGruppo ? '-gruppo' : ''}.csv`);
    } catch { toast.error('CSV non scaricato'); }
  };

  // Un mese scritto a mano nell'indirizzo resta nell'elenco finché non se ne sceglie un altro (il server dirà che
  // fuori dagli ultimi 12 non ci sono numeri)
  const elenco = mesi.some((m) => m.valore === mese) ? mesi : [{ valore: mese, etichetta: mese }, ...mesi];
  const scelta = (
    <label className="mese">Mese
      <select value={mese} onChange={(e) => cambiaMese(e.target.value)} aria-label="Mese">
        {elenco.map((m) => <option key={m.valore} value={m.valore}>{m.etichetta}</option>)}
      </select>
    </label>
  );

  // Il selettore della persona c'è solo se c'è qualcuno da scegliere: il collaboratore vede solo sé stesso, il
  // responsabile anche il suo gruppo, l'admin tutto lo staff (è il server a dire chi)
  const altri = persone.filter((p) => p.id !== io);
  const scelto = dati?.persona?.id || persona;
  const selettore = altri.length > 0 && (
    <div className="chi" role="group" aria-label="Di chi">
      <button type="button" className={!scelto || scelto === io ? 'on' : undefined} onClick={() => cambiaPersona('')}>I miei</button>
      {altri.map((p) => (
        <button type="button" key={p.id} className={scelto === p.id ? 'on' : undefined} onClick={() => cambiaPersona(p.id)}>
          <Iniziale persona={p} taglia={22} />{p.nome}
        </button>
      ))}
    </div>
  );

  const titoloMese = dati?.etichetta || etichettaMese(mese, mesi);
  const testa = () => (
    <div className="head r">
      <div>
        <div className="k">Il mese</div>
        <h1>{dati?.persona?.nome ? <>{dati.persona.nome}, <em>{titoloMese}</em></> : <em>{titoloMese}</em>}</h1>
        <p className="muto">Quanto è successo nel mese: niente email, telefoni o note dei clienti.</p>
      </div>
      <div className="strumenti">
        {scelta}
        {dati && <button type="button" className="btn p" onClick={() => scarica(false)}><Icona nome="scarica" />CSV</button>}
      </div>
    </div>
  );

  if (problema) {
    return (
      <main className="st wrap cruscotto-mese" data-testid="cruscotto-mese">
        {testa()}
        {selettore}
        <p className="vuoto" role="alert" data-testid="cruscotto-problema">{problema}</p>
      </main>
    );
  }

  if (!dati) {
    return (
      <main className="st wrap cruscotto-mese" data-testid="cruscotto-mese">
        {testa()}
        <p className="vuoto">Carico i numeri del mese…</p>
      </main>
    );
  }

  const clienti = dati.clienti || [];
  const tempi = dati.tempi || {};
  return (
    <main className="st wrap cruscotto-mese" data-testid="cruscotto-mese">
      {testa()}
      {selettore}

      <div className="numeri r" data-testid="numeri-mese">
        {NUMERI.map(([k, nome, colore]) => (
          <div key={k} className={colore || undefined}><b>{quanti(dati.numeri, k)}</b><span>{nome.toLowerCase()}</span></div>
        ))}
      </div>


      <QuotaDelMese mese={mese} persona={dati.persona?.id || persona} gia={dati.quota}
        nome={(dati.persona?.id || persona) && (dati.persona?.id || persona) !== io ? dati.persona?.nome : null} />

      <div className="g2" style={{ marginTop: '1rem' }}>
        <section className="card r">
          <div className="ttl"><div className="i"><Icona nome="torta" /></div><h2>Il mese in sei numeri</h2></div>
          <Barre numeri={dati.numeri} />
        </section>
        <section className="card r">
          <div className="ttl"><div className="i w"><Icona nome="orologio" /></div><h2>Tempi medi</h2></div>
          <div className="tempi">
            <div><b>{giorni(tempi.registrazione_proposta, tempi.casi_proposta)}</b>
              <span>dalla registrazione alla proposta{suCasi(tempi.casi_proposta)}</span></div>
            <div><b>{giorni(tempi.registrazione_rogito, tempi.casi_rogito)}</b>
              <span>dalla registrazione al rogito{suCasi(tempi.casi_rogito)}</span></div>
          </div>
          <p className="muto" style={{ fontSize: '.78rem', marginTop: '.6rem' }}>
            Media dei clienti arrivati a quel passo nel mese; «—» quando nel mese non ce n'è nessuno.
          </p>
        </section>
      </div>

      {gruppo && (
        <section className="card r" data-testid="gruppo-mese" style={{ marginTop: '1rem' }}>
          <div className="ttl"><div className="i b"><Icona nome="persone" /></div>
            {/* il gruppo si chiama col nome di chi lo guida («Il gruppo di Ernesto»), non con la chiave scritta nel database */}
            <h2>{gruppo.gruppo ? <>Il gruppo di <em>{gruppo.capo?.nome || gruppo.gruppo}</em></> : 'Tutto lo staff'} <small>{(gruppo.membri || []).length} persone</small></h2>
            <button type="button" className="link" onClick={() => scarica(true)}>CSV del gruppo</button>
          </div>
          <div className="tabella gruppo" role="table">
            <div className="riga testa" role="row">
              <span>Chi</span>{SEI.map((k) => <span key={k}>{nomeNumero(k)}</span>)}
            </div>
            <div className="riga totale" role="row">
              <span>{gruppo.gruppo ? 'Totale del gruppo' : 'Totale'}</span>{SEI.map((k) => <span key={k}>{quanti(gruppo.totale, k)}</span>)}
            </div>
            {(gruppo.membri || []).map((m) => (
              <div className="riga" role="row" key={m.id}>
                <span>{m.nome}</span>{SEI.map((k) => <span key={k}>{quanti(m.numeri, k)}</span>)}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="card r" data-testid="clienti-mese" style={{ marginTop: '1rem' }}>
        <div className="ttl"><div className="i"><Icona nome="persone" /></div><h2>I clienti del mese <small>{clienti.length}</small></h2></div>
        {!clienti.length && <p className="vuoto">Nessun cliente con qualcosa di fatto in questo mese.</p>}
        <div className="rows">
          {clienti.map((c) => (
            <div key={c.id} style={{ gridTemplateColumns: 'minmax(0,1fr) auto auto' }}>
              <div style={{ minWidth: 0 }}><b>{c.nome}</b><span>{testoEvento(c.ultimo_evento) || 'nessun evento nel mese'}</span></div>
              <span className={`tag ${tagFase(c.fase)}`}>{nomeFase(c.fase)}</span>
              <Link className="btn sm" to={`/admin/clienti/${c.id}`}>Apri</Link>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default CruscottoMese;
